import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box, Flex } from '@theme-ui/components';
import type { ObservableQuery } from '@apollo/client';

import { Button, getButtonStyles } from '@app/components/Button';
import Link from '@app/components/Link';
import Loader from '@app/components/Loader';
import { getUpgradeVariant } from '@app/pages/Pricing/helpers';
import type {
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct as ConfigurableMagentoProduct,
  PricingPageShopProduct_magento2_products_items,
} from '@app/pages/Pricing/hook/__generated__/PricingPageShopProduct';
import useUpgradePaths from '@app/pages/Pricing/hook/useUpgradePaths';
import type { AppTheme } from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import type {
  CustomerProductUpgradeQuery,
  CustomerProductUpgradeQueryVariables,
} from './__graphql__/WhatsNewForMe';
import IncludedProducts from './components/IncludedProducts';
import PricesTableBox from './components/PricesTableBox/PricesTableBox';
import NoUpgrades from './NoUpgrades';
import messages from './WhatsNewForMe.messages';

export type AuthenticatedProps = {
  title: string;
  includedProductsUrl?: string;
  productUpgradeData: CustomerProductUpgradeQuery;
  isLoading: boolean;
  magentoProduct?: PricingPageShopProduct_magento2_products_items | null;
} & Pick<
  ObservableQuery<
    CustomerProductUpgradeQuery,
    CustomerProductUpgradeQueryVariables
  >,
  'fetchMore'
>;

const Authenticated = ({
  title,
  productUpgradeData,
  fetchMore,
  isLoading,
  includedProductsUrl,
  magentoProduct,
}: AuthenticatedProps) => {
  const { url_key } = useParams<{
    url_key: string;
  }>();
  const { variants } = magentoProduct as ConfigurableMagentoProduct;
  const { loading: upgradeVariantsLoading, upgradeVariants } =
    useUpgradePaths();

  const upgradeVariant = useMemo(
    () => getUpgradeVariant(variants, upgradeVariants),
    [variants, upgradeVariants],
  );

  const upgradePrice =
    upgradeVariant?.product?.priceRange?.minimumPrice?.finalPrice;

  if (productUpgradeData?.userV2?.__typename === 'AuthenticatedUser') {
    const product_upgrade = productUpgradeData?.userV2.product_upgrade;
    const includedProducts = product_upgrade?.included_products;
    const includedProductsItems = includedProducts?.items || [];

    const pageInfo = includedProducts?.page_info;
    const currentPage = pageInfo?.current_page || 1;
    const hasNextPage = pageInfo?.has_next_page || false;

    const loadMore = () =>
      fetchMore({
        variables: { currentPage: (currentPage ?? 1) + 1 },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (
            fetchMoreResult?.userV2?.__typename === 'AuthenticatedUser' &&
            previousResult?.userV2?.__typename === 'AuthenticatedUser' &&
            hasNextPage &&
            fetchMoreResult?.userV2?.product_upgrade?.included_products?.items
          ) {
            const fetchMoreIncludedProducts =
              fetchMoreResult?.userV2?.product_upgrade?.included_products;

            const previousIncludedProducts =
              previousResult?.userV2?.product_upgrade?.included_products;

            fetchMoreIncludedProducts.items = [
              ...(previousIncludedProducts?.items || []),
              ...(fetchMoreIncludedProducts?.items || []),
            ];

            return fetchMoreResult;
          }

          return previousResult;
        },
      });

    const prices = product_upgrade?.prices;
    const currentProduct = product_upgrade?.current_product;
    const fullPriceValue = prices?.full_price?.value;
    let pricesSummary;

    if (upgradePrice) {
      const savingPriceValue =
        fullPriceValue &&
        upgradePrice?.value &&
        fullPriceValue - upgradePrice?.value;

      pricesSummary = {
        ...prices,
        upgrade_price: upgradePrice,
        savings_price: {
          currency: upgradePrice?.currency,
          value: savingPriceValue,
          __typename: 'Magento2_Money',
        },
      };
    }

    return (
      <Flex
        sx={{ justifyContent: 'center' }}
        data-testid="whats-new-for-me-authenticated"
        my={3}
      >
        {includedProductsItems && includedProductsItems.length ? (
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }} mb={5}>
            {upgradeVariantsLoading && (
              <Flex sx={{ justifyContent: 'center' }} mb={5}>
                <Loader />
              </Flex>
            )}

            {pricesSummary && currentProduct && !upgradeVariantsLoading && (
              <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
                <Box mb={4} mx={4} sx={{ justifyContent: 'center' }}>
                  <PricesTableBox
                    prices={pricesSummary}
                    ownedProduct={currentProduct}
                  />
                </Box>
                <Box mb={5}>
                  <Link
                    to={`/:language/pricing/${url_key}/`}
                    data-testid="whats-new-for-me-get-product-button"
                    css={(theme: AppTheme) =>
                      getButtonStyles({ type: 'primary', theme })
                    }
                  >
                    <FormattedMessage
                      {...messages.getProductButton}
                      values={{ product: title }}
                    />
                  </Link>
                </Box>
              </Flex>
            )}

            <Box mb={4} mx={4}>
              <IncludedProducts products={includedProductsItems} />
            </Box>

            {hasNextPage && (
              <Flex mb={rem(20)} sx={{ justifyContent: 'center' }}>
                <Button
                  type="secondary"
                  onClick={loadMore}
                  isLoading={isLoading}
                  disabled={isLoading}
                  data-testid="whats-new-show-more-products-button"
                >
                  <FormattedMessage {...messages.showMoreProducts} />
                </Button>
              </Flex>
            )}

            <Link
              to={`/:language/pricing/${url_key}/`}
              css={(theme: AppTheme) =>
                getButtonStyles({ type: 'primary', theme })
              }
              data-testid="whats-new-for-me-get-product-now-button"
            >
              <FormattedMessage
                {...messages.getProductNowButton}
                values={{ product: title }}
              />
            </Link>
          </Flex>
        ) : (
          <NoUpgrades includedProductsUrl={includedProductsUrl || ''} />
        )}
      </Flex>
    );
  }

  return null;
};

export default Authenticated;
